/* begin general import */
import { API_APP_USER_PREFIX } from "config/api-consts";
import { APP_USER_ROUTE } from "config/route-consts";
import { AppUserFilter } from "models/AppUser";
import { OrganizationFilter } from "models/Organization";
import { SexFilter } from "models/Sex";
import { StatusFilter } from "models/Status";
import React from "react";
import { useTranslation } from "react-i18next";
import { IdFilter, StringFilter } from "react3l-advanced-filters";
import { AdvanceTreeFilter } from "react3l-ui-library";
import AdvanceIdFilter from "react3l-ui-library/build/components/AdvanceFilter/AdvanceIdFilter";
import AdvanceStringFilter from "react3l-ui-library/build/components/AdvanceFilter/AdvanceStringFilter";
import Drawer, {
  DrawerProps,
} from "react3l-ui-library/build/components/Drawer/Drawer";
import { Reducer } from "redux";
import { appUserRepository } from "repositories/app-user-repository";
import { authorizationService } from "services/common-services/authorization-service";
import {
  FilterAction,
  filterReducer,
  filterService,
} from "services/page-services/filter-service";

/* end individual import */

export interface AppUserFilterProps extends DrawerProps {
  filter?: any;
  setVisible?: any;
  handleChangeAllFilter?: (data: any) => void;
}

function AppUserAdvanceFilter(props: AppUserFilterProps) {
  const [translate] = useTranslation();

  const { validAction } = authorizationService.useAuthorizedAction(
    APP_USER_ROUTE.split("/")[APP_USER_ROUTE.split("/").length - 1],
    API_APP_USER_PREFIX
  );

  const { visible, filter, setVisible, handleChangeAllFilter, handleClose } =
    props;

  const [modelFilter, setModelFilter] = React.useReducer<
    Reducer<AppUserFilter, FilterAction<AppUserFilter>>
  >(filterReducer, filter);

  const {
    handleChangeInputFilter,
    handleChangeSelectFilter,
    handleChangeTreeFilter,
  } = filterService.useFilter(filter, setModelFilter);

  const handleSaveModelFilter = React.useCallback(() => {
    handleChangeAllFilter(modelFilter);
    setVisible(false);
  }, [handleChangeAllFilter, modelFilter, setVisible]);

  const handleClearModelFilter = React.useCallback(() => {
    setModelFilter({
      type: 0,
      payload: new AppUserFilter(),
    });
  }, [setModelFilter]);

  return (
    <Drawer
      visible={visible}
      handleSave={handleSaveModelFilter}
      handleCancel={handleClearModelFilter}
      handleClose={handleClose}
      visibleFooter={true}
      loading={false}
      size={"sm"}
      title={translate("general.drawer.filter")}
      titleButtonCancel={translate("general.actions.clear")}
      titleButtonApply={translate("general.actions.apply")}
    >
      <div className="d-flex m-t--sm">
        <AdvanceStringFilter
          label={translate("appUsers.username")}
          showCount={true}
          maxLength={100}
          value={modelFilter["username"]["contain"]}
          onChange={handleChangeInputFilter({
            fieldName: "username",
            fieldType: "contain",
            classFilter: StringFilter,
          })}
          placeHolder={translate("appUsers.placeholder.username")}
        />
      </div>

      <div className="d-flex m-t--sm">
        <AdvanceStringFilter
          label={translate("appUsers.displayName")}
          showCount={true}
          maxLength={100}
          value={modelFilter["displayName"]["contain"]}
          onChange={handleChangeInputFilter({
            fieldName: "displayName",
            fieldType: "contain",
            classFilter: StringFilter,
          })}
          placeHolder={translate("appUsers.placeholder.displayName")}
        />
      </div>

      <div className="d-flex m-t--sm">
        <AdvanceStringFilter
          label={translate("appUsers.address")}
          showCount={true}
          maxLength={100}
          value={modelFilter["address"]["contain"]}
          onChange={handleChangeInputFilter({
            fieldName: "address",
            fieldType: "contain",
            classFilter: StringFilter,
          })}
          placeHolder={translate("appUsers.placeholder.address")}
        />
      </div>

      <div className="d-flex m-t--sm">
        <AdvanceStringFilter
          label={translate("appUsers.email")}
          showCount={true}
          maxLength={100}
          value={modelFilter["email"]["contain"]}
          onChange={handleChangeInputFilter({
            fieldName: "email",
            fieldType: "contain",
            classFilter: StringFilter,
          })}
          placeHolder={translate("appUsers.placeholder.email")}
        />
      </div>

      <div className="d-flex m-t--sm">
        <AdvanceStringFilter
          label={translate("appUsers.phone")}
          showCount={true}
          maxLength={100}
          value={modelFilter["phone"]["contain"]}
          onChange={handleChangeInputFilter({
            fieldName: "phone",
            fieldType: "contain",
            classFilter: StringFilter,
          })}
          placeHolder={translate("appUsers.placeholder.phone")}
        />
      </div>
      {validAction("filterListOrganization") && (
        <div className="d-flex m-t--sm">
          <AdvanceTreeFilter
            placeHolder={translate("appUsers.placeholder.organization")}
            classFilter={OrganizationFilter}
            onChange={handleChangeTreeFilter({
              fieldName: "organization",
              fieldType: "equal",
              classFilter: IdFilter,
            })}
            checkStrictly={true}
            item={modelFilter["organizationValue"]}
            getTreeData={appUserRepository.filterListOrganization}
            label={translate("appUsers.organization")}
            render={(item) =>
              item && item.id ? `${item?.code} - ${item?.name}` : null
            }
            type={0}
            searchProperty="search"
            searchType=""
          />
        </div>
      )}
      {validAction("filterListSex") && (
        <div className="d-flex m-t--sm">
          <AdvanceIdFilter
            value={modelFilter["sexValue"]}
            onChange={handleChangeSelectFilter({
              fieldName: "sex",
              fieldType: "equal",
              classFilter: IdFilter,
            })}
            classFilter={SexFilter}
            getList={appUserRepository.filterListSex}
            placeHolder={translate("appUsers.placeholder.sex")}
            label={translate("appUsers.sex")}
          />
        </div>
      )}
      {validAction("filterListStatus") && (
        <div className="d-flex m-t--sm">
          <AdvanceIdFilter
            value={modelFilter["statusValue"]}
            onChange={handleChangeSelectFilter({
              fieldName: "status",
              fieldType: "equal",
              classFilter: IdFilter,
            })}
            classFilter={StatusFilter}
            getList={appUserRepository.filterListStatus}
            placeHolder={translate("appUsers.placeholder.status")}
            label={translate("appUsers.status")}
          />
        </div>
      )}
    </Drawer>
  );
}

export default AppUserAdvanceFilter;
